import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Plus } from "lucide-react";
import {
  clinicSessionsApi,
  clinicSessionsQueryKeys,
  DEFAULT_SESSION_SUGGESTIONS,
  type ClinicSession,
} from "../../shared/api/clinicSessions";
import { apiError } from "../../shared/api/client";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { PageHeader } from "../../shared/ui/PageHeader";
import { TimeModeCard } from "./TimeModeCard";
import { BookingReadinessCard } from "./BookingReadinessCard";
import { bookingSettingsQueryKeys } from "../../shared/api/bookingSettings";
import { bookingPolicyApi, bookingPolicyQueryKeys } from "../../shared/api/bookingPolicy";
import { TimeInput } from "../../shared/ui/TimeInput";
import {
  clinicSettingsApi,
  clinicSettingsQueryKeys,
  parseWorkingHours,
} from "../../shared/api/clinic";
import { sessionOutsideMessage } from "../../shared/workingHours";
import { freeGaps, latestEndFor, sessionOverlapMessage } from "./sessionOverlap";
import { sessionCodeFrom, slotCount, slotLeftover } from "./sessionSlots";
import { SessionTimeline } from "./SessionTimeline";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** Cài đặt Buổi khám — các khung buổi trong ngày (Sáng/Chiều/Tối) dùng cho đặt lịch. */
export function SessionsSettingsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ClinicSession | "new" | null>(null);
  // #19979 — xoá đi qua dialog của hệ thống, không qua window.confirm.
  const [deleting, setDeleting] = useState<ClinicSession | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);

  const query = useQuery({
    queryKey: clinicSessionsQueryKeys.all(),
    queryFn: () => clinicSessionsApi.list(),
  });

  /*
   * Bộ buổi khám engine đặt lịch THẬT SỰ đọc (#19688 mục 3).
   *
   * Màn này liệt kê bản ghi mức phòng khám (`list()` không kèm branchId), còn engine ưu tiên bản
   * ghi của chi nhánh. Phòng khám nào có bản ghi chi nhánh sẽ ngồi sửa những dòng KHÔNG có tác
   * dụng gì, và những dòng đang thật sự quyết định khung giờ thì không hiện ra để bật/tắt. Đo
   * được ở pk-luongngoc (Ty, 16–21/10). Không đổi nghĩa danh sách, chỉ nói ra sự thật.
   */
  const effective = useQuery({
    queryKey: clinicSessionsQueryKeys.effective(),
    queryFn: () => clinicSessionsApi.effective(),
  });

  // #19953 — theo giờ bắt đầu, đúng thứ tự trong ngày. Không còn ô "Thứ tự": nó chỉ từng có tác
  // dụng phân xử khi các buổi chồng giờ, và chồng giờ nay bị chặn.
  const rows = useMemo(() => {
    return (query.data ?? [])
      .slice()
      .sort(
        (a, b) =>
          a.startTime.localeCompare(b.startTime) || a.label.localeCompare(b.label, "vi")
      );
  }, [query.data]);

  const total = query.data?.length ?? 0;
  const activeCount = (query.data ?? []).filter((s) => s.active).length;

  const clinic = useQuery({
    queryKey: clinicSettingsQueryKeys.current(),
    queryFn: () => clinicSettingsApi.get(),
    staleTime: 60_000,
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: clinicSessionsQueryKeys.all() });
    qc.invalidateQueries({ queryKey: bookingSettingsQueryKeys.readiness() });
  }

  const disable = useMutation({
    mutationFn: (id: string) => clinicSessionsApi.disable(id),
    onSuccess: refresh,
  });
  const enable = useMutation({
    mutationFn: (id: string) => clinicSessionsApi.enable(id),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => clinicSessionsApi.remove(id),
    onSuccess: () => {
      setBulkError(null);
      setDeleting(null);
      refresh();
    },
    onError: (e: unknown) => {
      setBulkError(apiError(e).detail);
      setDeleting(null);
    },
  });
  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete([...sel.selectedIds], clinicSessionsApi.remove);
    setBulkPending(false);
    sel.clear();
    refresh();
    setBulkError(
      failed.length
        ? `${failed.length} buổi khám không xoá được (đang được dùng).`
        : null,
    );
  }

  const quickAdd = useMutation({
    mutationFn: async () => {
      for (const s of DEFAULT_SESSION_SUGGESTIONS) {
        await clinicSessionsApi.create({
          code: s.code,
          label: s.label,
          startTime: s.startTime,
          endTime: s.endTime,
        });
      }
    },
    onSuccess: refresh,
  });

  // Cùng queryKey với TimeModeCard: lưu cách chọn giờ xong thì mô tả trang đổi theo.
  const policyQ = useQuery({
    queryKey: bookingPolicyQueryKeys.all(),
    queryFn: () => bookingPolicyApi.get(),
  });
  // Cột "Số khung" và xem trước trong hộp thoại chỉ có nghĩa ở chế độ Giờ cụ thể (đang LƯU).
  const slotMinutes = policyQ.data?.timeMode === "SLOT" ? policyQ.data.slotMinutes : null;

  return (
    <div>
      {/* #19977 — mô tả theo cách chọn giờ ĐANG LƯU: ở chế độ Giờ cụ thể các dòng dưới đây là khoảng
          để cắt khung, không phải "buổi" mà bệnh nhân chọn. */}
      <PageHeader
        title="Khung giờ nhận lịch"
        description={
          policyQ.data?.timeMode === "SLOT"
            ? `Bệnh nhân chọn giờ cụ thể, cắt mỗi ${policyQ.data.slotMinutes} phút trong các khoảng giờ dưới đây. Ngoài các khoảng này không nhận lịch.`
            : "Bệnh nhân chọn một trong các buổi dưới đây (VD: Sáng, Chiều, Tối). Ngoài các buổi này không nhận lịch."
        }
        actions={
          <Button type="button" onClick={() => setEditing("new")}>
            <Plus />
            Tạo buổi khám
          </Button>
        }
      />

      {/* #19978 — Lịch làm việc bác sĩ ở Nhân sự & Quyền (mỗi đường dẫn chỉ ở MỘT nhóm menu), nên
          trỏ sang từ đây: khung giờ quyết định giờ nhận lịch, lịch trực quyết định bác sĩ nào. */}
      <p className="-mt-2 mb-3 text-sm text-muted-foreground" data-testid="schedules-link">
        Bác sĩ nào nhận khung nào do{" "}
        <a href="/settings/schedules" className="text-primary underline">
          Lịch làm việc (bác sĩ)
        </a>{" "}
        quyết định.
      </p>

      {/* #19935 — Hình thức giờ dời về đây, đứng TRÊN danh sách buổi mà nó áp vào. */}
      <TimeModeCard />

      {/* #19939 TC08 — bật đặt online mà không có giờ nào mở thì nói ra ngay tại đây. */}
      <BookingReadinessCard />

      {effective.data?.tier === "BRANCH" && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <p className="font-medium">
            Danh sách dưới đây KHÔNG phải bộ đang áp dụng cho đặt lịch.
          </p>
          <p className="mt-1">
            Chi nhánh đang dùng có {effective.data.sessions.length} buổi khám riêng, và engine đặt
            lịch đọc bộ đó:{" "}
            <span className="font-medium">
              {effective.data.sessions
                .map((s) => `${s.label} ${s.startTime}–${s.endTime}`)
                .join(" · ")}
            </span>
            . Các dòng bên dưới là buổi khám mức phòng khám, chỉ được dùng khi chi nhánh chưa khai
            buổi nào — nên sửa chúng lúc này sẽ không đổi khung giờ bệnh nhân thấy.
          </p>
        </div>
      )}
      {effective.data?.tier === "NONE" && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <p className="font-medium">Chưa có buổi khám nào đang kích hoạt.</p>
          <p className="mt-1">
            Ngày nào không có Phân ca thì đặt lịch online sẽ không mở khung giờ nào — hệ thống
            không còn tự dựng 07:00–20:00 nữa.
          </p>
        </div>
      )}

      {/* #19979 — ba thẻ thống kê chiếm chỗ mà không giúp quyết định gì; một dòng là đủ. */}
      {total > 0 && (
        <p className="mb-2 text-sm text-muted-foreground" data-testid="sessions-count">
          {total} khoảng · {activeCount} đang nhận lịch
          {total - activeCount > 0 ? ` · ${total - activeCount} đã tắt` : ""}
        </p>
      )}

      <SessionTimeline
        sessions={query.data ?? []}
        workingHours={parseWorkingHours(clinic.data?.workingHoursJson ?? null)}
      />

      {query.isError && (
        <p className="text-sm text-destructive">Không tải được danh sách buổi khám.</p>
      )}
      {query.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}

      {bulkError && <p className="mt-3 text-sm text-destructive">{bulkError}</p>}
      {!query.isLoading && rows.length > 0 && (
        <>
          <div className="mt-4">
            <BulkDeleteBar
              count={sel.count}
              noun="buổi khám"
              pending={bulkPending}
              onClear={sel.clear}
              onDelete={bulkDelete}
            />
          </div>
          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-9 text-center">
                    <input
                      type="checkbox"
                      aria-label="Chọn tất cả"
                      className="h-4 w-4 rounded border-input align-middle accent-primary"
                      checked={rows.length > 0 && rows.every((s) => sel.selectedIds.has(s.id))}
                      ref={(el) => {
                        if (el)
                          el.indeterminate =
                            rows.some((s) => sel.selectedIds.has(s.id)) &&
                            !rows.every((s) => sel.selectedIds.has(s.id));
                      }}
                      onChange={() => {
                        const next = new Set(sel.selectedIds);
                        if (rows.every((s) => next.has(s.id)))
                          rows.forEach((s) => next.delete(s.id));
                        else rows.forEach((s) => next.add(s.id));
                        sel.setSelectedIds(next);
                      }}
                    />
                  </TableHead>
                  <TableHead>Tên buổi</TableHead>
                  <TableHead>Giờ</TableHead>
                  {slotMinutes != null && <TableHead>Số khung</TableHead>}
                  <TableHead>Nhận lịch</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-center">
                      <input
                        type="checkbox"
                        aria-label="Chọn dòng"
                        className="h-4 w-4 rounded border-input align-middle accent-primary"
                        checked={sel.selectedIds.has(s.id)}
                        onChange={() => sel.toggle(s.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <strong>{s.label}</strong>
                      {s.afterHours && (
                        <Badge variant="muted" className="ml-2">
                          Ngoài giờ làm việc
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">
                      {s.startTime}–{s.endTime}
                    </TableCell>
                    {slotMinutes != null && (
                      <TableCell data-testid="slot-count-cell">
                        <span className="font-medium">
                          {slotCount(s.startTime, s.endTime, slotMinutes)}
                        </span>
                        {(() => {
                          const left = slotLeftover(s.startTime, s.endTime, slotMinutes);
                          return left ? (
                            <span className="block text-xs text-amber-700 dark:text-amber-300">
                              {left.from}–{left.to} không dùng được
                            </span>
                          ) : null;
                        })()}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          aria-label={`Nhận lịch: ${s.label}`}
                          checked={s.active}
                          disabled={disable.isPending || enable.isPending}
                          onCheckedChange={(on) =>
                            on ? enable.mutate(s.id) : disable.mutate(s.id)
                          }
                        />
                        <span className="text-xs text-muted-foreground">
                          {s.active ? "Đang nhận" : "Đã tắt"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(s)}
                        >
                          Sửa
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              aria-label={`Thao tác khác: ${s.label}`}
                            >
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-destructive"
                              onSelect={() => setDeleting(s)}
                            >
                              Xoá
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {!query.isLoading && rows.length === 0 && (
        <Card className="mt-4">
          <CardContent className="p-6 text-muted-foreground">
            <p className="mt-0">
              Chưa có buổi khám nào. Bạn có thể thêm nhanh 3 buổi mặc định
              (Sáng · Chiều · Tối) hoặc tự tạo.
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                disabled={quickAdd.isPending}
                onClick={() => quickAdd.mutate()}
              >
                {quickAdd.isPending
                  ? "Đang thêm…"
                  : "Thêm nhanh Sáng / Chiều / Tối"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing("new")}>
                Tự tạo buổi khám
              </Button>
            </div>
            {quickAdd.isError && (
              <p className="mt-3 text-sm text-destructive">
                {apiError(quickAdd.error).detail}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Xoá “{deleting?.label}”?</DialogTitle>
            <DialogDescription>
              Xoá vĩnh viễn, không thể hoàn tác. Muốn tạm dừng nhận lịch thì tắt công tắc “Nhận
              lịch” thay vì xoá.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleting(null)}>
              Huỷ
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              {remove.isPending ? "Đang xoá…" : "Xoá"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editing && (
        <SessionFormModal
          item={editing === "new" ? null : editing}
          sessions={query.data ?? []}
          slotMinutes={slotMinutes}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function SessionFormModal({
  item,
  sessions,
  slotMinutes,
  onClose,
  onSaved,
}: {
  item: ClinicSession | null;
  /** Các buổi cùng danh sách — để chặn chồng giờ ngay khi nhập (#19953). */
  sessions: readonly ClinicSession[];
  /** Độ dài khung khi đang ở chế độ Giờ cụ thể; null = chế độ Buổi. */
  slotMinutes: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(item?.label ?? "");
  // #19979 — mã tự sinh từ tên cho buổi MỚI (mã không đổi được sau khi tạo, nên buổi cũ giữ mã).
  const code = item?.code ?? sessionCodeFrom(label, sessions.map((x) => x.code));
  const [startTime, setStartTime] = useState(item?.startTime ?? "");
  const [endTime, setEndTime] = useState(item?.endTime ?? "");
  // #19936 — sức chứa không còn khai ở Buổi khám và không được đọc ở đâu; giữ nguyên giá trị cũ.
  const capacity = item?.capacity ?? null;
  const [afterHours, setAfterHours] = useState<boolean>(item?.afterHours ?? false);
  const [error, setError] = useState<string | null>(null);

  // #19938 — báo NGAY khi nhập, không đợi bấm Tạo: giờ ngược, hoặc vượt giờ làm việc (trừ Buổi
  // ngoài giờ). Server vẫn kiểm lại khi lưu.
  const clinic = useQuery({
    queryKey: clinicSettingsQueryKeys.current(),
    queryFn: () => clinicSettingsApi.get(),
    staleTime: 60_000,
  });
  const wh = parseWorkingHours(clinic.data?.workingHoursJson ?? null);
  // #19953 — chồng giờ chỉ xét khi buổi này đang bật (hoặc đang tạo mới), khớp server: một buổi đã
  // ngưng được sửa tự do và bị kiểm lại lúc kích hoạt.
  const checksOverlap = !item || item.active;
  const selfId = item?.id ?? null;
  const latestEnd = checksOverlap ? latestEndFor(sessions, selfId, startTime) : null;
  const startInside =
    checksOverlap && startTime && !latestEnd
      ? sessions.find(
          (o) => o.active && o.id !== selfId && o.startTime <= startTime && startTime < o.endTime
        )
      : undefined;
  const liveError =
    startTime && endTime && startTime >= endTime
      ? "Giờ bắt đầu phải trước giờ kết thúc."
      : startInside
        ? `Giờ bắt đầu nằm trong buổi ${startInside.label} (${startInside.startTime}–${startInside.endTime}). Các buổi khám không được chồng giờ nhau.`
        : (checksOverlap && sessionOverlapMessage(sessions, selfId, startTime, endTime)) ||
          sessionOutsideMessage(wh, startTime, endTime, afterHours);
  const gaps = checksOverlap ? freeGaps(sessions, selfId) : [];

  const save = useMutation({
    mutationFn: async () => {
      const cap = capacity;
      if (item) {
        return clinicSessionsApi.update(item.id, {
          label: label.trim(),
          startTime,
          endTime,
          capacity: cap,
          afterHours,
        });
      }
      return clinicSessionsApi.create({
        code: code.trim(),
        label: label.trim(),
        startTime,
        endTime,
        capacity: cap,
        afterHours,
      });
    },
    onSuccess: onSaved,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{item ? "Sửa buổi khám" : "Tạo buổi khám mới"}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="session-label">Tên buổi *</Label>
              <Input
                id="session-label"
                required
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="VD: Sáng"
              />
              {label.trim() && (
                <p className="text-xs text-muted-foreground" data-testid="session-code-hint">
                  Mã: <span className="font-mono">{code}</span>
                  {item ? " (không đổi sau khi tạo)" : " (tự sinh từ tên)"}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="session-start">Giờ bắt đầu *</Label>
                <TimeInput
                  id="session-start"
                  required
                  value={startTime}
                  onChange={setStartTime}
                />
              </div>
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="session-end">Giờ kết thúc *</Label>
                <TimeInput
                  id="session-end"
                  required
                  value={endTime}
                  onChange={setEndTime}
                />
              </div>
            </div>
            {gaps.length > 0 && (
              <p data-testid="session-free-gaps" className="-mt-1 text-xs text-muted-foreground">
                Khoảng còn trống:{" "}
                <span className="font-mono">
                  {gaps.map((g) => `${g.start}–${g.end}`).join(" · ")}
                </span>
                {latestEnd && !liveError && (
                  <>
                    {" "}— bắt đầu {startTime} thì kết thúc muộn nhất{" "}
                    <span className="font-mono">{latestEnd}</span>.
                  </>
                )}
              </p>
            )}
            {liveError && (
              <p role="alert" data-testid="session-live-error" className="-mt-1 text-sm text-destructive">
                {liveError}
              </p>
            )}
            {slotMinutes != null && !liveError && startTime && endTime && (
              <p data-testid="session-slot-preview" className="-mt-1 text-xs text-muted-foreground">
                Khung {slotMinutes} phút: {slotCount(startTime, endTime, slotMinutes)} khung
                {(() => {
                  const left = slotLeftover(startTime, endTime, slotMinutes);
                  return left ? `; ${left.from}–${left.to} không dùng được.` : ".";
                })()}
              </p>
            )}
            {/* #19935/#19938 — từ #19953 các buổi không còn chồng giờ được, nên tác dụng DUY NHẤT của cờ
                này là cho buổi vượt Giờ làm việc (WorkingHoursCeiling). Nhãn nói đúng điều đó (#19977). */}
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={afterHours}
                onChange={(e) => setAfterHours(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <span>
                <span className="font-medium">Nhận lịch ngoài Giờ làm việc</span>
                <span className="block text-xs text-muted-foreground">
                  Bật khi khoảng giờ này nằm ngoài Giờ làm việc đã khai (VD: tối 17:30–23:00). Nếu
                  nằm trong Giờ làm việc thì không cần bật.
                </span>
              </span>
            </label>
          </div>

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Đóng
            </Button>
            <Button
              type="submit"
              disabled={
                save.isPending ||
                !label.trim() ||
                !startTime ||
                !endTime ||
                !!liveError
              }
            >
              {save.isPending
                ? "Đang lưu…"
                : item
                  ? "Lưu thay đổi"
                  : "Tạo buổi khám"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
